import { api } from '@shared/api';

/** GET /notifications item. */
export interface NotificationView {
  id: string;
  event_type: string;
  template_key: string;
  category: string;
  urgency: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  deep_link: string | null;
  created_at: string;
  read_at: string | null;
}

export interface NotificationsPage {
  data: NotificationView[];
  next_cursor: string | null;
  has_more: boolean;
}

export const NOTIFICATION_CATEGORIES = [
  'sessions',
  'schedule_changes',
  'learning',
  'recordings',
  'enrollment_billing',
  'messages',
  'announcements',
  'account_security',
  'system',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];
export const CHANNELS = ['in_app', 'email', 'push'] as const;
export type Channel = (typeof CHANNELS)[number];
export type ChannelPrefs = Record<Channel, boolean>;

/** GET /users/me/notification-preferences (per org and user). */
export interface NotificationPreferences {
  organization_id: string;
  categories: Partial<Record<NotificationCategory, ChannelPrefs>>;
  /** Channels that cannot be turned off, per category (in_app always; email for account_security). */
  locked: Partial<Record<NotificationCategory, Channel[]>>;
  channels: { email: boolean; push: boolean };
  quiet_hours: { start: string; end: string } | null;
  digest: { enabled: boolean; hour_local: number };
  guardian_copy: boolean;
  language: 'ar' | 'en' | 'fr' | null;
  timezone: string;
  customized: boolean;
}

/** GET /notifications/push/public-key: VAPID applicationServerKey; `enabled` is the org's web_push flag. */
export interface PushPublicKey {
  enabled: boolean;
  public_key: string | null;
}

/** PushSubscription.toJSON() as posted to /notifications/push/subscriptions. */
export interface PushSubscriptionBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  expirationTime?: number | null;
}

/** POST /notifications/push/test result. */
export interface PushTestResult {
  devices: number;
  sent: number;
  revoked: number;
  failed: number;
}

/** PUT body: partial; omitted fields keep their value. */
export interface NotificationPreferencesIn {
  categories?: Partial<Record<NotificationCategory, Partial<ChannelPrefs>>>;
  quiet_hours?: { start: string; end: string } | null;
  digest?: { enabled?: boolean; hour_local?: number };
  guardian_copy?: boolean;
  channels?: { email?: boolean; push?: boolean };
}

export const notificationsApi = {
  list: (params: { unread?: boolean; limit?: number; cursor?: string | null } = {}) => {
    const q = new URLSearchParams();
    if (params.unread) q.set('unread', 'true');
    if (params.limit) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    const s = q.toString();
    return api.get<NotificationsPage>(`/notifications${s ? `?${s}` : ''}`);
  },
  unreadCount: () => api.get<{ count: number }>('/notifications/unread-count'),
  read: (id: string) => api.post<void>(`/notifications/${encodeURIComponent(id)}/read`),
  readAll: () => api.post<void>('/notifications/read-all'),
  preferences: () => api.get<NotificationPreferences>('/users/me/notification-preferences'),
  savePreferences: (body: NotificationPreferencesIn) => api.put<NotificationPreferences>('/users/me/notification-preferences', body),
  pushPublicKey: () => api.get<PushPublicKey>('/notifications/push/public-key'),
  pushSubscribe: (body: PushSubscriptionBody) => api.post<{ id: string }>('/notifications/push/subscriptions', body),
  // The endpoint is a capability URL: sent in the body, never in the query string.
  pushUnsubscribe: (endpoint: string) => api.request<void>('/notifications/push/subscriptions', { method: 'DELETE', body: { endpoint } }),
  pushTest: () => api.post<PushTestResult>('/notifications/push/test'),
};

export const notificationKeys = {
  all: ['notifications'] as const,
  count: (orgId: string | null) => ['notifications', 'count', orgId] as const,
  latest: (orgId: string | null) => ['notifications', 'latest', orgId] as const,
  list: (orgId: string | null, unread: boolean) => ['notifications', 'list', orgId, unread] as const,
  prefs: (orgId: string | null) => ['notifications', 'preferences', orgId] as const,
  pushKey: (orgId: string | null) => ['notifications', 'push-key', orgId] as const,
};

export const BELL_POLL_MS = 30_000;
